/**
 * ThemeToggle - 3-state cycle (system -> light -> dark -> system).
 *
 * MD-31 ASKLAW-EMBER-THEME Phase 1, spec §3.3.
 *
 * Behavior:
 *  - Read localStorage.theme on mount. Falsy -> "system".
 *  - Apply document.documentElement.dataset.theme = resolved.
 *    resolved = "dark" | "light" (KHONG "system" - resolve real value first).
 *  - Listen prefers-color-scheme media query change KHI state === "system".
 *  - On click cycle state, persist localStorage, apply data-theme.
 *
 * SSG safe: useState init from localStorage wrapped in mounted flag.
 *   Server render: default "system" + Monitor icon (matches FOUC inline
 *   script default of dark when system=dark, but icon stays Monitor so no
 *   UI mismatch since we only flip [data-theme] on html element, not icon).
 *
 * Inline SVG icon (KHONG add lucide-react dep). Stroke 1.5px, 18px viewport.
 */

import { useEffect, useState, useCallback } from 'react';
import { useLang } from '../use-lang';

type Theme = 'system' | 'light' | 'dark';

function resolveTheme(t: Theme): 'light' | 'dark' {
  if (t === 'system') {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return 'light';
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return t;
}

function applyTheme(t: Theme): void {
  if (typeof document === 'undefined') return;
  const resolved = resolveTheme(t);
  document.documentElement.setAttribute('data-theme', resolved);
}

export function ThemeToggle() {
  const { lang } = useLang();
  // Init: default "system" cho SSG. Sau mount thi sync tu localStorage.
  const [theme, setTheme] = useState<Theme>('system');
  const [mounted, setMounted] = useState(false);

  // Mount: read localStorage + apply
  useEffect(() => {
    let stored: Theme = 'system';
    try {
      const raw = localStorage.getItem('theme');
      if (raw === 'light' || raw === 'dark' || raw === 'system') {
        stored = raw;
      }
    } catch {
      // localStorage blocked - giu default
    }
    setTheme(stored);
    setMounted(true);
    applyTheme(stored);
  }, []);

  // Listen prefers-color-scheme change KHI state === "system"
  useEffect(() => {
    if (!mounted || theme !== 'system') return;
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => applyTheme('system');
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
    // Safari < 14 fallback
    mq.addListener(handler);
    return () => mq.removeListener(handler);
  }, [mounted, theme]);

  const cycle = useCallback(() => {
    setTheme((prev) => {
      const next: Theme = prev === 'system' ? 'light' : prev === 'light' ? 'dark' : 'system';
      try {
        localStorage.setItem('theme', next);
      } catch {
        // ignore quota / privacy mode
      }
      applyTheme(next);
      return next;
    });
  }, []);

  // i18n label
  const t = {
    aria:
      lang === 'en'
        ? `Toggle theme (current: ${theme})`
        : `Đổi giao diện (hiện tại: ${themeLabelVi(theme)})`,
    titleSystem: lang === 'en' ? 'System' : 'Theo hệ thống',
    titleLight: lang === 'en' ? 'Light' : 'Sáng',
    titleDark: lang === 'en' ? 'Dark' : 'Tối',
  };
  const title = theme === 'system' ? t.titleSystem : theme === 'light' ? t.titleLight : t.titleDark;

  return (
    <button
      type="button"
      onClick={cycle}
      aria-label={t.aria}
      title={title}
      style={{
        width: 36,
        height: 36,
        border: '1px solid var(--asklaw-border)',
        background: 'transparent',
        borderRadius: 'var(--radius-full, 9999px)',
        color: 'var(--asklaw-ink)',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        transition: 'background var(--motion-fast, 140ms) ease, border-color var(--motion-fast, 140ms) ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'var(--asklaw-paper-sunk)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      {/* Render icon SAU mount de avoid SSR hydration mismatch.
       * Phase 1.5: wrap in span.theme-toggle__icon voi key=theme.
       * React re-mount on theme change re-fires CSS pop animation. */}
      {mounted ? (
        <span key={theme} className="theme-toggle__icon">
          <ThemeIcon theme={theme} />
        </span>
      ) : (
        <span className="theme-toggle__icon">
          <MonitorIcon />
        </span>
      )}
    </button>
  );
}

function themeLabelVi(t: Theme): string {
  if (t === 'system') return 'theo hệ thống';
  if (t === 'light') return 'sáng';
  return 'tối';
}

function ThemeIcon({ theme }: { theme: Theme }) {
  if (theme === 'system') return <MonitorIcon />;
  if (theme === 'light') return <SunIcon />;
  return <MoonIcon />;
}

function SunIcon() {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2" />
      <path d="M12 20v2" />
      <path d="m4.93 4.93 1.41 1.41" />
      <path d="m17.66 17.66 1.41 1.41" />
      <path d="M2 12h2" />
      <path d="M20 12h2" />
      <path d="m6.34 17.66-1.41 1.41" />
      <path d="m19.07 4.93-1.41 1.41" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

function MonitorIcon() {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  );
}
